import React, { useState, ChangeEvent, FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../Context/AuthContext';
import { getUserProject, getProjectMembers } from '../Services/UserProjectService';
import { UserRole } from '../Services/AuthService';

// Interfaces para tipado de estados internos
interface Credentials {
  email: string;
  password: string;
  remember: boolean;
}

interface Errors {
  email?: string;
  password?: string;
}

const LoginForm: React.FC = () => {
  // Obtenemos las funciones y estado del contexto de autenticación
  const { login, error: authError, isLoading, getRoleDashboardUrl } = useAuth();
  const navigate = useNavigate();
  
  // Estados locales del formulario
  const [credentials, setCredentials] = useState<Credentials>({
    email: '',
    password: '',
    remember: false
  });
  
  const [validationErrors, setValidationErrors] = useState<Errors>({});

  // Aviso de sesión caducada (lo marca el interceptor 401 antes del logout)
  const [sessionExpired, setSessionExpired] = useState<boolean>(false);

  // Handler con tipos definidos para el evento
  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const { name, value, type, checked } = e.target;
    setCredentials({
      ...credentials,
      [name]: type === 'checkbox' ? checked : value
    });

    // Ocultar el aviso de sesión caducada en cuanto el usuario empieza a reintentar
    if (sessionExpired) {
      setSessionExpired(false);
    }

    // Clear errors when typing
    if (validationErrors[name as keyof Errors]) {
      setValidationErrors({
        ...validationErrors,
        [name]: undefined
      });
    }
  };
  
  // Función de validación que devuelve un objeto de tipo Errors
  const validate = (): Errors => {
    const newErrors: Errors = {};
    
    if (!credentials.email) {
      newErrors.email = 'Por favor, introduce tu email';
    } else if (!/\S+@\S+\.\S+/.test(credentials.email)) {
      newErrors.email = 'El formato de email no es válido';
    }
    
    if (!credentials.password) {
      newErrors.password = 'Por favor, introduce tu contraseña';
    } else if (credentials.password.length < 6) {
      newErrors.password = 'La contraseña debe tener al menos 6 caracteres';
    }
    
    return newErrors;
  };
  
  // Handler para el evento submit con tipo definido
  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    
    // Llamamos a la función login del contexto
    try {
      await login(credentials.email, credentials.password, async (user) => {
        // Si es usuario USER, verificar si tiene miembros antes de redirigir
        if (user.role === UserRole.USER) {
          try {
            // Obtener el proyecto del usuario
            const project = await getUserProject();

            if (project?.projectId) {
              // Verificar si tiene miembros
              const members = await getProjectMembers(project.projectId);

              if (members.length === 0) {
                // No tiene miembros - redirigir a crear miembros
                navigate('/user/members');
                return;
              }
            }
          } catch (error) {
            console.error('Error checking members:', error);
            // Si falla la verificación, continuar al dashboard normal
          }
        }

        // Tiene miembros o no es USER - redirigir al dashboard normal
        const dashboardUrl = getRoleDashboardUrl(user.role);
        navigate(dashboardUrl);
      });
      
      // Si remember está marcado, podríamos guardar el email en localStorage
      if (credentials.remember) {
        localStorage.setItem('rememberedEmail', credentials.email);
      } else {
        localStorage.removeItem('rememberedEmail');
      }
    } catch (error) {
      // El manejo de errores se hace en el contexto
      console.error('Error al iniciar sesión:', error);
    }
  };

  // Cargar email recordado al montar el componente
  React.useEffect(() => {
    const rememberedEmail = localStorage.getItem('rememberedEmail');
    if (rememberedEmail) {
      setCredentials(prev => ({
        ...prev,
        email: rememberedEmail,
        remember: true
      }));
    }

    // Si llegamos aquí por un token caducado, mostrar el aviso una sola vez
    if (localStorage.getItem('sessionExpired')) {
      setSessionExpired(true);
      localStorage.removeItem('sessionExpired');
    }
  }, []);

  return (
    <div className="login-card">
      <div className="login-card-body">
        {/* Logo como título */}
        <div className="text-center mb-4">
          <img src="/logos/logo-complet.avif" alt="Logo" className="logo" style={{maxWidth: '100%'}} />
        </div>

        {/* Aviso de sesión caducada (no se muestra si ya hay un error de login en curso) */}
        {sessionExpired && !authError && (
          <div className="alert alert-warning" role="alert">
            Tu sesión ha caducado. Por favor, vuelve a iniciar sesión.
          </div>
        )}

        {/* Mostrar error de autenticación si existe */}
        {authError && (
          <div className="alert alert-danger" role="alert">
            {authError}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Email Input */}
          <div className="mb-3">
            <label htmlFor="email" className="form-label">Email</label>
            <input
              type="email"
              className={`form-control ${validationErrors.email ? 'is-invalid' : ''}`}
              id="email"
              name="email"
              value={credentials.email}
              onChange={handleChange}
              placeholder="tu@email.com"
              disabled={isLoading}
            />
            {validationErrors.email && (
              <div className="invalid-feedback">
                {validationErrors.email}
              </div>
            )}
          </div>

          {/* Password Input */}
          <div className="mb-3">
            <label htmlFor="password" className="form-label">Contraseña</label>
            <input
              type="password"
              className={`form-control ${validationErrors.password ? 'is-invalid' : ''}`}
              id="password"
              name="password"
              value={credentials.password}
              onChange={handleChange}
              placeholder="••••••••"
              disabled={isLoading}
            />
            {validationErrors.password && (
              <div className="invalid-feedback">
                {validationErrors.password}
              </div>
            )}
          </div>

          {/* Remember Me Checkbox */}
          <div className="mb-3 form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="remember"
              name="remember"
              checked={credentials.remember}
              onChange={handleChange}
              disabled={isLoading}
            />
            <label className="form-check-label" htmlFor="remember">
              Recordarme
            </label>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="btn btn-primary w-100 py-2 mt-3"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Iniciando sesión...
              </>
            ) : 'Iniciar sesión'}
          </button>
        </form>

        {/* Forgot Password and Sign Up Links */}
        <div className="text-center mt-3">
          <Link to="/forgot-password" className="link-primary d-block mb-2">
            ¿Olvidaste tu contraseña?
          </Link>
          <p className="text-muted mt-3 mb-0">
            ¿No tienes una cuenta?{' '}
            <a href="#" className="link-primary">
              Regístrate
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;